<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import CompanyModel from '@models/Company';
import Contacts from '@views/companies/contacts';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.companies'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    Contacts,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'name', label: this.$t('table_labels.name'), sortable: true },
        { key: 'street', label: this.$t('table_labels.street'), sortable: true },
        { key: 'postal_code', label: this.$t('table_labels.postal_code'), sortable: true },
        { key: 'town', label: this.$t('table_labels.town'), sortable: true },
        { key: 'country', label: this.$t('table_labels.country'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        name: null,
      },
      previewedCompany: null,
      showModal: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    itemsProvider(ctx) {
      return CompanyModel.orderBy(this.sortDir + ctx.sortBy)
        .where('name', this.filters.name)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.company') }))) {
        this.$wait.start('company processing');
        const company = new CompanyModel({ id });
        await company.delete();
        this.$wait.end('company processing');
        this.$refs.asyncTable.refresh();
      }
    },
    async previewCompany(id) {
      this.$wait.start('company processing');
      CompanyModel.find(id).then((response) => {
        this.previewedCompany = Object.assign({}, response.company);
        this.showModal = true;
        this.$wait.end('company processing');
      });
    },
    closePreview() {
      this.previewedCompany = null;
      this.showModal = false;
    },
    updateFilter: debounce(function (value) {
      this.filters.name = value;
    }, 500),
    clearFilters() {
      this.filters.name = null;
    },
    goToEditCompany(companyId) {
      const routeData = this.$router.resolve({ name: 'companies.edit', params: { id: companyId } });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.companies')" :button-text="$t('titles.create_company')" @button-pressed="$router.push({ name: 'companies.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span>&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [$t('search.entities.name')].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.name" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.name" @click="clearFilters">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="reports-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('company processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="previewCompany(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'eye']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="goToEditCompany(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
    <b-modal
      v-if="previewedCompany"
      ref="company-preview-modal"
      v-model="showModal"
      size="lg"
      hide-footer
      :title="previewedCompany.name"
      static
      @hide="closePreview"
    >
      <div class="row">
        <Contacts v-if="previewedCompany.contacts" :data="previewedCompany.contacts" :is-modal="true" />
      </div>
    </b-modal>
  </Layout>
</template>
